import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { showToast } from '../components/Toast';
import './Login.css';
import { DSIcon, DSInput, DSButton } from '../design-system/primitives';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function ChangePassword({ isRequired = false, onPasswordChanged }) {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const token = authService.getToken();
      await axios.post(
        `${API_URL}/auth/change-password`,
        {
          currentPassword,
          newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Update user object to remove requirePasswordChange flag
      const user = authService.getCurrentUser();
      if (user) {
        user.requirePasswordChange = false;
        localStorage.setItem('user', JSON.stringify(user));
      }

      showToast('Password changed successfully!', 'success');
      
      // Notify parent component if callback provided
      if (onPasswordChanged) {
        onPasswordChanged();
      }
      
      // Force a page reload to update App state
      window.location.href = '/my-membership';
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/logo.png" alt="Capital Pinball" className="login-logo" />
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <h2 style={{ color: 'var(--ds-color-textDefault)', marginBottom: '10px', fontSize: '20px', textAlign: 'center' }}>
            {isRequired ? 'Change Your Password' : 'Update Password'}
          </h2>

          {isRequired && (
            <div style={{
              backgroundColor: 'var(--ds-color-statusWarningBg)',
              border: '1px solid var(--ds-color-statusWarningText)',
              padding: '12px',
              borderRadius: '5px',
              marginBottom: '20px',
              fontSize: '14px',
              color: 'var(--ds-color-statusWarningText)'
            }}>
              <strong><DSIcon name="warning" /> Required:</strong> You must change your temporary password before continuing.
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <DSInput label="Current Password"
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoFocus
              disabled={isLoading}
              placeholder="Enter your temporary password" />

          <DSInput label="New Password"
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength="8"
              disabled={isLoading}
              placeholder="At least 8 characters" />

          <DSInput label="Confirm New Password"
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength="8"
              disabled={isLoading}
              placeholder="Re-enter your new password" />

          <div style={{
            backgroundColor: 'var(--ds-color-statusInfoBg)',
            padding: '12px',
            borderRadius: '5px',
            marginBottom: '20px',
            fontSize: '13px',
            color: 'var(--ds-color-statusInfoText)'
          }}>
            <strong>Password Tips:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Use at least 8 characters</li>
              <li>Mix letters, numbers, and symbols</li>
              <li>Avoid common words</li>
            </ul>
          </div>

          <DSButton tone="primary" appearance="solid" className="login-button" type="submit" disabled={isLoading}>
            {isLoading ? 'Changing Password...' : 'Change Password'}
          </DSButton>

          {!isRequired && (
            <DSButton tone="secondary" appearance="solid" className="login-button" onClick={() => navigate(-1)} disabled={isLoading} style={{ marginTop: '10px' }}>
              Cancel
            </DSButton>
          )}
        </form>
      </div>
    </div>
  );
}

export default ChangePassword;
